import * as PopoverPrimitive from '@radix-ui/react-popover';
import { type ReactNode } from 'react';
import { cn } from '@/lib/cn';

interface PopoverProps {
  trigger: ReactNode;
  children: ReactNode;
  side?: 'top' | 'right' | 'bottom' | 'left';
  align?: 'start' | 'center' | 'end';
  className?: string;
  open?: boolean;
  onOpenChange?: (open: boolean) => void;
  sideOffset?: number;
}

/**
 * Lightweight Popover wrapper. Origin-aware reveal animation matches
 * Radix' `data-state` so the popup feels anchored to the trigger.
 */
export function Popover({
  trigger,
  children,
  side = 'bottom',
  align = 'center',
  className,
  open,
  onOpenChange,
  sideOffset = 8,
}: PopoverProps) {
  return (
    <PopoverPrimitive.Root open={open} onOpenChange={onOpenChange}>
      <PopoverPrimitive.Trigger asChild>{trigger}</PopoverPrimitive.Trigger>
      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          side={side}
          align={align}
          sideOffset={sideOffset}
          collisionPadding={12}
          className={cn(
            'z-[1100] w-72 rounded-lg border border-border bg-surface-elevated p-3 text-foreground shadow-soft outline-none',
            'data-[state=open]:animate-in data-[state=closed]:animate-out',
            'data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
            'data-[state=closed]:zoom-out-95 data-[state=open]:zoom-in-95',
            'data-[side=top]:slide-in-from-bottom-1 data-[side=bottom]:slide-in-from-top-1',
            'data-[side=left]:slide-in-from-right-1 data-[side=right]:slide-in-from-left-1',
            'duration-150 ease-[cubic-bezier(0.22,1,0.36,1)]',
            className,
          )}
        >
          {children}
          <PopoverPrimitive.Arrow className="fill-surface-elevated stroke-border" />
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
}
